import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PhoneFormField } from '@/components/PhoneFormField';
import { FormField, FormSection } from '@/components/owner-ui';
import { useCreateCustomer } from '@/hooks/mutations/useCustomerMutations';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';
import { getDefaultPhoneCountry } from '@/lib/country';
import type { Customer } from '@/types/sale';
import { cn } from '@/lib/utils';

interface CustomerWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onCreated?: (customer: Customer | null) => void;
}

const TOTAL_STEPS = 3;

export function CustomerWizard({
  open,
  onOpenChange,
  initialName = '',
  onCreated,
}: CustomerWizardProps) {
  const { t } = useTranslation('customers');
  const { t: tc } = useTranslation('common');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState({
    name: initialName,
    phone: '',
    email: '',
    address: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createCustomer = useCreateCustomer();
  const { data: shopDetailResponse } = useCurrentShopDetail();
  const defaultPhoneCountry = getDefaultPhoneCountry(shopDetailResponse?.data?.country);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setDirection(1);
    setErrors({});
    setForm({
      name: initialName,
      phone: '',
      email: '',
      address: '',
      notes: '',
    });
  }, [initialName, open]);

  const summaryItems = useMemo(
    () => [
      { label: t('name'), value: form.name || '-' },
      { label: t('phone'), value: form.phone || '-' },
      { label: t('email'), value: form.email || t('wizard_optional_empty') },
      { label: t('address'), value: form.address || t('wizard_optional_empty') },
      { label: t('notes'), value: form.notes || t('wizard_optional_empty') },
    ],
    [form.address, form.email, form.name, form.notes, form.phone, t],
  );

  const handleClose = () => {
    onOpenChange(false);
  };

  const validateIdentity = () => {
    const nextErrors: Record<string, string> = {};

    // Au moins un identifiant — nom OU téléphone — pour reconnaître le client plus tard.
    if (!form.name.trim() && !form.phone.trim()) {
      const message = t('validation_name_or_phone_required', {
        defaultValue: 'Renseignez au moins un nom ou un numéro de téléphone.',
      });
      nextErrors.name = message;
      nextErrors.phone = message;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDetails = () => {
    const nextErrors: Record<string, string> = {};

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = t('validation_email_invalid');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    const isValid = step === 0 ? validateIdentity() : validateDetails();
    if (!isValid) return;

    setDirection(1);
    setStep((current) => Math.min(current + 1, TOTAL_STEPS - 1));
  };

  const handleBack = () => {
    if (step === 0) {
      handleClose();
      return;
    }

    setDirection(-1);
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateIdentity() || !validateDetails()) return;

    try {
      const response = await createCustomer.mutateAsync({
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      });

      toast.success(t('customer_created'));
      onCreated?.((response.data as Customer | null) ?? null);
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : tc('error_generic');
      toast.error(message);
    }
  };

  if (!open) return null;

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col pt-safe-top">
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-slate-200 px-4 dark:border-charcoal-700">
        <button
          type="button"
          onClick={handleClose}
          aria-label={tc('close')}
          className="grid h-9 w-9 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-1 dark:text-charcoal-300 dark:hover:bg-charcoal-800 dark:hover:text-charcoal-50"
        >
          <X className="size-4" strokeWidth={2.25} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-charcoal-950 dark:text-charcoal-50">
            {t('wizard_title')}
          </p>
        </div>
        <span className="text-xs font-medium text-slate-500 dark:text-charcoal-300">
          {t('wizard_step_counter', { current: step + 1, total: TOTAL_STEPS })}
        </span>
      </div>

      <div className="flex items-center justify-center gap-1.5 py-3">
        {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
          <div
            key={index}
            className={
              index === step
                ? 'h-1.5 w-7 rounded-full bg-charcoal-950 dark:bg-charcoal-50'
                : index < step
                  ? 'h-1.5 w-2 rounded-full bg-lime-500'
                  : 'h-1.5 w-2 rounded-full bg-slate-300 dark:bg-charcoal-600'
            }
          />
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: direction * 48 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -48 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
          >
            {step === 0 && (
              <FormSection
                title={t('wizard_identity_title')}
                description={t('wizard_identity_description')}
              >
                <FormField label={t('name')} htmlFor="customer-name" error={errors.name}>
                  <Input
                    id="customer-name"
                    value={form.name}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, name: event.target.value }))
                    }
                    autoComplete="name"
                  />
                </FormField>
                <PhoneFormField
                  label={t('phone')}
                  value={form.phone}
                  onChange={(value) => setForm((current) => ({ ...current, phone: value }))}
                  error={errors.phone}
                  defaultCountry={defaultPhoneCountry}
                  required
                />
              </FormSection>
            )}

            {step === 1 && (
              <FormSection
                title={t('wizard_details_title')}
                description={t('wizard_details_description')}
              >
                <FormField
                  label={t('email')}
                  htmlFor="customer-email"
                  optional
                  error={errors.email}
                >
                  <Input
                    id="customer-email"
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, email: event.target.value }))
                    }
                    autoComplete="email"
                    inputMode="email"
                  />
                </FormField>
                <FormField label={t('address')} htmlFor="customer-address" optional>
                  <Input
                    id="customer-address"
                    value={form.address}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, address: event.target.value }))
                    }
                    autoComplete="street-address"
                  />
                </FormField>
                <FormField label={t('notes')} htmlFor="customer-notes" optional>
                  <Textarea
                    id="customer-notes"
                    value={form.notes}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, notes: event.target.value }))
                    }
                    placeholder={t('wizard_notes_placeholder')}
                  />
                </FormField>
              </FormSection>
            )}

            {step === 2 && (
              <FormSection
                title={t('wizard_confirm_title')}
                description={t('wizard_confirm_description')}
              >
                <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-700 dark:bg-card">
                  {summaryItems.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 last:border-b-0 dark:border-charcoal-700"
                    >
                      <span className="text-sm text-slate-500 dark:text-charcoal-300">
                        {item.label}
                      </span>
                      <span className="max-w-[60%] text-right text-sm font-medium text-charcoal-950 dark:text-charcoal-50">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </FormSection>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        className={cn(
          'border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm pb-[calc(env(safe-area-inset-bottom)+12px)] dark:border-charcoal-700 dark:bg-charcoal-950/95',
        )}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-4 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 dark:border-charcoal-700 dark:text-charcoal-200 dark:hover:bg-charcoal-800"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
            {step === 0 ? t('close') : tc('back')}
          </button>
          {step < TOTAL_STEPS - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-lime-400 px-4 text-[13px] font-semibold text-charcoal-950 transition-shadow hover:shadow-[0_6px_16px_-8px_rgba(163,230,53,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2"
            >
              {tc('next')}
              <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createCustomer.isPending}
              className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-lime-400 px-4 text-[13px] font-semibold text-charcoal-950 transition-shadow hover:shadow-[0_6px_16px_-8px_rgba(163,230,53,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {createCustomer.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
              ) : (
                <Check className="h-4 w-4" strokeWidth={2.25} />
              )}
              {tc('save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
